import { useState, useMemo } from 'react';
import { Card, SubjectType } from '../types/Card';

interface UseCardFiltersReturn {
  filteredCards: Card[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedSubject: SubjectType | 'all';
  setSelectedSubject: (subject: SubjectType | 'all') => void;
  showPinnedOnly: boolean;
  setShowPinnedOnly: (show: boolean) => void;
  hasActiveFilters: boolean;
  clearFilters: () => void;
}

export function useCardFilters(cards: Card[]): UseCardFiltersReturn {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubject, setSelectedSubject] = useState<SubjectType | 'all'>('all');
  const [showPinnedOnly, setShowPinnedOnly] = useState(false);

  const filteredCards = useMemo(() => {
    let result = [...cards];

    // Filter by search term
    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase().trim();
      result = result.filter((card) =>
        card.topic.toLowerCase().includes(lowerSearchTerm)
      );
    }

    // Filter by subject
    if (selectedSubject !== 'all') {
      result = result.filter((card) => card.subject === selectedSubject);
    }

    // Filter by pinned status
    if (showPinnedOnly) {
      result = result.filter((card) => card.isPinned);
    }

    return result;
  }, [cards, searchTerm, selectedSubject, showPinnedOnly]);

  const hasActiveFilters = useMemo(() => {
    return searchTerm.trim() !== '' || selectedSubject !== 'all' || showPinnedOnly;
  }, [searchTerm, selectedSubject, showPinnedOnly]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedSubject('all');
    setShowPinnedOnly(false);
  };

  return {
    filteredCards,
    searchTerm,
    setSearchTerm,
    selectedSubject,
    setSelectedSubject,
    showPinnedOnly,
    setShowPinnedOnly,
    hasActiveFilters,
    clearFilters,
  };
}
